import React, { useRef, Children } from 'react';
import { motion, useInView, Variants } from 'framer-motion';
type AnimationVariant = 'fadeUp' | 'fadeIn' | 'slideLeft' | 'slideRight' | 'scaleUp' | 'blurIn';
interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  variant?: AnimationVariant;
}
const variantConfigs: Record<AnimationVariant, {
  initial: object;
  animate: object;
}> = {
  fadeUp: {
    initial: {
      opacity: 0,
      y: 24
    },
    animate: {
      opacity: 1,
      y: 0
    }
  },
  fadeIn: {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    }
  },
  slideLeft: {
    initial: {
      opacity: 0,
      x: -40
    },
    animate: {
      opacity: 1,
      x: 0
    }
  },
  slideRight: {
    initial: {
      opacity: 0,
      x: 40
    },
    animate: {
      opacity: 1,
      x: 0
    }
  },
  scaleUp: {
    initial: {
      opacity: 0,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      scale: 1
    }
  },
  blurIn: {
    initial: {
      opacity: 0,
      filter: 'blur(8px)'
    },
    animate: {
      opacity: 1,
      filter: 'blur(0px)'
    }
  }
};
export function AnimatedSection({
  children,
  className = '',
  delay = 0,
  variant = 'fadeUp'
}: AnimatedSectionProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const config = variantConfigs[variant];
  return <motion.div ref={ref} initial={config.initial} animate={isInView ? config.animate : config.initial} transition={{
    duration: 0.6,
    ease: 'easeOut',
    delay
  }} className={className} data-id="element-81">
      {children}
    </motion.div>;
}
interface AnimatedChildrenProps {
  children: React.ReactNode;
  className?: string;
  staggerDelay?: number;
}
export function AnimatedChildren({
  children,
  className = '',
  staggerDelay = 0.1
}: AnimatedChildrenProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <motion.div ref={ref} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay
      }
    }
  }} className={className} data-id="element-82">
      {children}
    </motion.div>;
}
export function AnimatedChild({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.div variants={{
    hidden: {
      opacity: 0,
      y: 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  }} className={className} data-id="element-83">
      {children}
    </motion.div>;
}